import jwtDecode from "jwt-decode";
import { AccessTypeWithStaff } from "../../Enums/UserEnums";
import type { HelpCenterAudience } from "../../interfaces/models/IHelpCenter";
import {
  HELP_CENTER_STAFF_PORTAL_QUERY,
  HELP_CENTER_STAFF_PORTAL_VALUE,
} from "./helpCenterRoutes";

function getCookie(name: string): string | null {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1, c.length);
    }
    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length, c.length);
    }
  }
  return null;
}

export function getStoredWsgPermission(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const raw =
      sessionStorage.getItem("token_decoded") || getCookie("token_decoded");
    if (raw) {
      const parsed = JSON.parse(raw) as { extension_WSG_Permissions?: string };
      return parsed.extension_WSG_Permissions;
    }

    const token = sessionStorage.getItem("token") || getCookie("token");
    if (!token) {
      return undefined;
    }

    const payload = jwtDecode(token) as { extension_WSG_Permissions?: string };
    return payload.extension_WSG_Permissions;
  } catch {
    return undefined;
  }
}

export function resolveWsgPermission(
  tokenPermission?: string,
): string | undefined {
  return tokenPermission ?? getStoredWsgPermission();
}

export function getVisibleHelpCenterAudiences(
  permission: string | undefined,
  firmAdminAccess?: boolean,
): HelpCenterAudience[] {
  const audiences: HelpCenterAudience[] = ["members"];

  const isDataAdmin = permission === AccessTypeWithStaff.dataAdmin;
  const isStaff =
    permission === AccessTypeWithStaff.wsgStaff ||
    permission === AccessTypeWithStaff.wsgSuper;

  if (isDataAdmin || firmAdminAccess) {
    audiences.push("dataAdmin");
  }

  if (isStaff) {
    audiences.push("staff");
  }

  return audiences;
}

export function isHelpCenterStaff(permission: string | undefined): boolean {
  return (
    permission === AccessTypeWithStaff.wsgStaff ||
    permission === AccessTypeWithStaff.wsgSuper
  );
}

export function isJwtHelpCenterAccessResolved(
  permission: string | undefined,
): boolean {
  return (
    permission === AccessTypeWithStaff.dataAdmin || isHelpCenterStaff(permission)
  );
}

export type HelpCenterPortal = "member" | "staff";

export function resolveHelpCenterPortal(
  searchParams: URLSearchParams,
  isStaff: boolean,
): HelpCenterPortal {
  if (
    isStaff &&
    searchParams.get(HELP_CENTER_STAFF_PORTAL_QUERY) ===
      HELP_CENTER_STAFF_PORTAL_VALUE
  ) {
    return "staff";
  }

  return "member";
}

const MEMBER_PORTAL_AUDIENCES: HelpCenterAudience[] = ["members", "dataAdmin"];

export function getMemberPortalAudiences(): HelpCenterAudience[] {
  return MEMBER_PORTAL_AUDIENCES;
}

export function canViewHelpCenterCategory(
  audience: HelpCenterAudience,
  visibleAudiences: HelpCenterAudience[],
): boolean {
  if (MEMBER_PORTAL_AUDIENCES.includes(audience)) {
    return true;
  }

  return visibleAudiences.includes(audience);
}
